import { getValidatedLimitParam, getValidatedPageParam, useCustomToast } from '@elkaso-app/web-design';
import http from 'apis/config/http-service';
import { useQueryWrapper } from 'apis/config/use-api-wrapper';
import type { AxiosError } from 'axios';
import { useIsLoggedIn } from 'hooks/use-is-logged-in';
import { TParam, usePageParams } from 'hooks/use-page-params';
import { UseQueryOptions, UseQueryResult } from 'react-query';

const endpoint = 'ums/v1/cms/supplier-profiles';
export const getSupplierProfilesEndpointIdentifier = endpoint;

export interface SupplierProfileSupplierDto {
  id: number;
  name: string | null;
  cutOffTime: string | null;
  minimumOrder: number | null;
  countryCode: string;
  email: string | null;
  isActive: boolean;
  isPublic: boolean;
  imageUrl: string | null;
}

export interface SupplierProfileEmailConfig {
  to: string;
  cc?: string[];
  bcc?: string[];
}

export interface SupplierProfileDto {
  id: number;
  businessName: string;
  category: string | null;
  moq: string | null;
  delivery: string | null;
  paymentTerms: string | null;
  commissionRate: number | null;
  supplierId: number | null;
  supplier: SupplierProfileSupplierDto | null;
  emailConfig: SupplierProfileEmailConfig | null;
  whatsappNumbers: string[];
  /** Bill To block on generated proforma PDFs. ⚠️ Not returned by this endpoint yet (BE follow-up
   * pending) — typed optimistically so form hydration lights up when it lands. */
  billingLegalName?: string | null;
  billingAddress?: string | null;
  taxRegistrationNo?: string | null;
}

interface ResponseData {
  data: SupplierProfileDto[];
  count: number;
  total: number;
  page: number;
  pageCount: number;
  hasNext: boolean;
  hasPrev: boolean;
}

type TData = ResponseData;
type TError = AxiosError;
type TQueryKey = [typeof endpoint, { page: TParam; limit: TParam; name: TParam }];
type Options = Omit<UseQueryOptions<unknown, TError, TData, TQueryKey>, 'queryKey' | 'queryFn'>;
type ReturnType = UseQueryResult<TData, TError>;

export function useGetSupplierProfilesApi(options?: Options): ReturnType {
  const toast = useCustomToast();
  const page = getValidatedPageParam();
  const limit = getValidatedLimitParam();
  const isLoggedIn = useIsLoggedIn();
  const { getPageParams } = usePageParams();
  const { name } = getPageParams();

  const queryFn = async () => {
    try {
      const { data } = await http.get(endpoint, { params: { page, limit, name } });
      return data;
    } catch (error: any) {
      if (!error.response?.status || !error.response?.data?.message) throw error;

      toast({
        description: error.response.data.message,
        status: 'error',
      });

      throw error;
    }
  };

  return useQueryWrapper([endpoint, { page, limit, name }], queryFn, {
    enabled: isLoggedIn,
    ...options,
  });
}
